import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, X, Building, Trash2, ChevronsUpDown } from 'lucide-react';
import type { Ingredient } from '@/types/ingredient.types';
import type { Supplier } from '@/types/suppliers.types';
import { suppliersService } from '@/services/suppliers.service';
import { toast } from 'sonner';

interface IngredientSuppliersModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ingredient: Ingredient | null;
}

export const IngredientSuppliersModal: React.FC<IngredientSuppliersModalProps> = ({
  open,
  onOpenChange,
  ingredient
}) => {
  const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>([]);
  const [assignedSuppliers, setAssignedSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (ingredient && open) {
      loadSuppliers();
    }
  }, [ingredient, open]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);

      // Get all active suppliers
      const allSuppliers = await suppliersService.getActiveSuppliers();

      // Split suppliers into assigned and available based on ingredient's suppliers
      const assignedIds = ingredient?.suppliers?.map(sup => sup.supplierId) || [];
      const assigned = allSuppliers.filter(sup => assignedIds.includes(sup.id));
      const available = allSuppliers.filter(sup => !assignedIds.includes(sup.id));

      setAssignedSuppliers(assigned);
      setAvailableSuppliers(available);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      toast.error('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSupplier = async () => {
    if (!selectedSupplier || !ingredient) {
      toast.error('Selecciona un proveedor');
      return;
    }

    setLoading(true);
    try {
      const supplierToAssign = availableSuppliers.find(sup => sup.id === selectedSupplier);

      if (!supplierToAssign) {
        toast.error('Proveedor no encontrado');
        return;
      }

      // Create supplier ingredient object
      const supplierIngredients = [{
        ingredientId: ingredient.id,
        supplierId: selectedSupplier,
        isActive: true,
        price: 0,
        deliveryDays: 5,
        minimumQuantity: 1,
        notes: ''
      }];

      const response = await suppliersService.updateSupplierIngredients(selectedSupplier, supplierIngredients);

      if (response.success) {
        toast.success('Proveedor asignado correctamente');

        // Move supplier from available to assigned
        const remaining = availableSuppliers.filter(sup => sup.id !== selectedSupplier);
        setAssignedSuppliers([...assignedSuppliers, supplierToAssign]);
        setAvailableSuppliers(remaining);
        setSelectedSupplier('');
        setSearchTerm('');
        setShowDropdown(false);
      } else {
        toast.error('Error al asignar proveedor');
      }
    } catch (error) {
      console.error('Error assigning supplier:', error);
      toast.error('Error al asignar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSupplier = async (supplierId: string) => {
    if (!ingredient) return;

    try {
      setLoading(true);
      const response = await suppliersService.removeSupplierIngredient(supplierId, ingredient.id);

      if (response.success) {
        const supplierToRemove = assignedSuppliers.find(sup => sup.id === supplierId);
        if (supplierToRemove) {
          // Move from assigned back to available
          setAssignedSuppliers(assignedSuppliers.filter(sup => sup.id !== supplierId));
          setAvailableSuppliers([...availableSuppliers, supplierToRemove]);
        }
        toast.success('Proveedor desvinculado correctamente');
      } else {
        toast.error('Error al desvincular proveedor');
      }
    } catch (error) {
      console.error('Error removing supplier:', error);
      toast.error('Error al desvincular proveedor');
    } finally {
      setLoading(false);
    }
  };

  if (!ingredient) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Building className="w-5 h-5 mr-2" />
            Proveedores de "{ingredient.nombre || ingredient.name}"
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Supplier Section */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Buscar Proveedor</h4>

            {availableSuppliers.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Building className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-gray-500">Todos los proveedores están asignados</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-3 items-end">
                  <div className="flex-1 relative">
                    <Label htmlFor="supplier-search">Seleccionar Proveedor</Label>
                    <div className="relative">
                      <Input
                        id="supplier-search"
                        type="text"
                        placeholder="Buscar proveedores..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setShowDropdown(true);
                        }}
                        onFocus={() => {
                          setShowDropdown(true);
                          if (!searchTerm && selectedSupplier) {
                            const supplier = availableSuppliers.find(sup => sup.id === selectedSupplier);
                            if (supplier) {
                              setSearchTerm(supplier.businessName);
                            }
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setShowDropdown(false), 150);
                        }}
                        className="pr-10"
                      />
                      <ChevronsUpDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    </div>

                    {/* Dropdown Results */}
                    {showDropdown && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {searchTerm ? (
                          // Filtered results when searching
                          availableSuppliers
                            .filter(sup =>
                              sup.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              sup.ruc.includes(searchTerm)
                            )
                            .slice(0, 20)
                            .map((supplier) => (
                              <div
                                key={supplier.id}
                                className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                                  selectedSupplier === supplier.id ? 'bg-rose-50 border-rose-200' : ''
                                }`}
                                onClick={() => {
                                  setSelectedSupplier(supplier.id);
                                  setSearchTerm(supplier.businessName);
                                  setShowDropdown(false);
                                }}
                              >
                                <div className="font-medium text-gray-900">{supplier.businessName}</div>
                                <div className="text-sm text-gray-500">RUC: {supplier.ruc}</div>
                              </div>
                            ))
                        ) : (
                          // Show first 20 suppliers when no search term
                          availableSuppliers
                            .slice(0, 20)
                            .map((supplier) => (
                              <div
                                key={supplier.id}
                                className={`p-3 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 ${
                                  selectedSupplier === supplier.id ? 'bg-rose-50 border-rose-200' : ''
                                }`}
                                onClick={() => {
                                  setSelectedSupplier(supplier.id);
                                  setSearchTerm(supplier.businessName);
                                  setShowDropdown(false);
                                }}
                              >
                                <div className="font-medium text-gray-900">{supplier.businessName}</div>
                                <div className="text-sm text-gray-500">RUC: {supplier.ruc}</div>
                              </div>
                            ))
                        )}

                        {searchTerm && availableSuppliers.filter(sup =>
                          sup.businessName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          sup.ruc.includes(searchTerm)
                        ).length === 0 && (
                          <div className="p-3 text-center text-gray-500">
                            No se encontraron proveedores
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleAssignSupplier}
                    disabled={!selectedSupplier || loading}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {loading ? 'Asignando...' : 'Asignar'}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Assigned Suppliers Section */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Proveedores Asignados ({assignedSuppliers.length})</h4>

            {assignedSuppliers.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Building className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-gray-500">No hay proveedores asignados</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assignedSuppliers.map((supplier) => (
                  <div key={supplier.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <p className="font-medium text-green-900">{supplier.businessName}</p>
                      <p className="text-sm text-green-600">RUC: {supplier.ruc}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveSupplier(supplier.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};